// src/components/AssignAssessModal.js
import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { usePrivateDealAssignment } from "../hooks/usePrivateDealAssignment";
import UserSelect from "./UserSelect";

// dealId / isPrivate: optional deal context for private-deal assignment rules. When the
// application belongs to a private deal, non-owner/admin assigners only see deal members;
// the deal's owner/admin sees everyone (non-members tagged) and can grant Contributor
// access on the fly. Callers without deal context (business units) omit both — unchanged.
const AssignAssessModal = ({ isOpen, onClose, applicationId, refreshData, apiEndpoint, dealId, isPrivate }) => {
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const apiCall = useApi();

    // Private-deal assignment rules (inert when no dealId / deal is public)
    const { isPrivateDeal, memberIds, canManagePrivateDeal, grantDealContributor } =
        usePrivateDealAssignment(isOpen, dealId, isPrivate);

    // Fetch users when modal opens
    useEffect(() => {
        if (isOpen) fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const fetchUsers = async () => {
        try {
            // Use the assignableUsers endpoint to get users with Contributor+ role
            const res = await apiCall("GET", "/assessments/assignableUsers");
            setUsers(res || []);
        } catch (err) {} finally {
            setLoadingUsers(false);
        }
    };

    // Called after a successful invite — no immediate PUT, just let UserSelect handle it
    const handleInviteSuccess = () => {
        // No-op: the invited user is added to the dropdown and auto-selected by UserSelect.
        // The actual assignment happens when the user clicks Save.
    };

    // Handle save assignment
    const handleSave = async (e) => {
        e.preventDefault();
        setIsProcessing(true);

        const isInvite = selectedUserId.startsWith("invite:");
        const payload = isInvite
            ? { assignedToUserInviteId: parseInt(selectedUserId.split(":")[1]) }
            : { assignedToUserId: parseInt(selectedUserId) };

        try {
            // Private deal + owner/admin assigning a non-member: confirm, then grant
            // CONTRIBUTOR@DEAL before the assignment (the backend rejects non-members).
            if (isPrivateDeal && canManagePrivateDeal && !isInvite && memberIds
                && !memberIds.has(parseInt(selectedUserId))) {
                const selected = users.find(u => String(u.id) === String(selectedUserId));
                const assigneeName = selected?.fullName || selected?.email || `User #${selectedUserId}`;
                const confirmed = window.confirm(
                    `${assigneeName} can't see this private deal. Grant Contributor access and assign?`
                );
                if (!confirmed) {
                    setIsProcessing(false);
                    return;
                }
                await grantDealContributor(parseInt(selectedUserId));
            }

            const endpoint = apiEndpoint || `/applications/${applicationId}/assign`;
            await apiCall("PUT", endpoint, payload);
            refreshData?.();
            onClose();
        } catch (err) {
            alert("Failed to assign assessment. Check console for details.");
        } finally {
            setIsProcessing(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border border-border max-w-lg w-full shadow-lg rounded-md bg-theme-secondary">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-content-primary">
                        Assign Assessment
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSave} className="space-y-4">
                    {/* User Dropdown */}
                    <div>
                        <label className="block text-sm font-medium text-content-secondary mb-2">
                            Assign To
                        </label>
                        <UserSelect
                            value={selectedUserId}
                            onChange={setSelectedUserId}
                            users={(isPrivateDeal && !canManagePrivateDeal && memberIds)
                                ? users.filter(u => memberIds.has(u.id))
                                : users}
                            loading={loadingUsers}
                            canInvite={!isPrivateDeal || canManagePrivateDeal}
                            nonMemberIds={(isPrivateDeal && canManagePrivateDeal && memberIds)
                                ? new Set(users.filter(u => !memberIds.has(u.id)).map(u => u.id))
                                : undefined}
                            onInviteSuccess={handleInviteSuccess}
                        />
                        {isPrivateDeal && !canManagePrivateDeal && (
                            <p className="text-xs text-gray-500 mt-1">
                                Only the deal owner can invite external users to a private deal.
                            </p>
                        )}
                        {isPrivateDeal && canManagePrivateDeal && (
                            <p className="text-xs text-gray-500 mt-1">
                                Invited users will be granted access to this private deal when they join.
                            </p>
                        )}
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-end space-x-3 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isProcessing || loadingUsers || !selectedUserId}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isProcessing ? "Assigning..." : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AssignAssessModal;
